import { ArrowLeft, X } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "../app/navigation.js";
import { levelProgress } from "@guandan/protocol";
import { useLocale } from "../i18n.js";
import "./primitives.css";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  busy?: boolean;
}

export function Button({
  variant = "secondary",
  size = "md",
  busy = false,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={`btn btn-${variant} btn-${size}${busy ? " btn-busy" : ""}${className ? ` ${className}` : ""}`}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="btn-spinner" aria-hidden="true" /> : null}
      <span className="btn-label">{children}</span>
    </button>
  );
}

export function Panel({
  children,
  className,
  tone = "default",
  ...rest
}: {
  children: ReactNode;
  className?: string;
  tone?: "default" | "accent" | "felt" | "quiet";
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`panel panel-${tone}${className ? ` ${className}` : ""}`} {...rest}>
      {children}
    </div>
  );
}

/**
 * Level ring with the XP bar drawn as its stroke.
 *
 * The ring shows progress *within* the current level rather than lifetime XP,
 * because "how close am I to levelling" is the only question it can usefully
 * answer at this size.
 */
export function LevelRing({
  level,
  xp,
  size = 44,
}: {
  level: number;
  xp: number;
  size?: number;
}) {
  const { locale } = useLocale();
  const progress = levelProgress(level, xp);

  const r = size / 2 - 3;
  const circumference = 2 * Math.PI * r;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="level-ring"
      role="img"
      aria-label={
        locale === "zh-CN"
          ? `等级 ${level}，距下一级 ${Math.round(progress * 100)}%`
          : `Level ${level}, ${Math.round(progress * 100)} percent to next`
      }
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--bg-sunk)"
        strokeWidth="3"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--gold)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${circumference * progress} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{
          transition: "stroke-dasharray var(--dur-slow) var(--ease-out)",
        }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={size * 0.36}
        fontWeight="700"
        fill="var(--ink)"
        className="tabular"
      >
        {level}
      </text>
    </svg>
  );
}

export function Coins({
  amount,
  className,
  size = 16,
}: {
  amount: number;
  className?: string;
  size?: number;
}) {
  return (
    <span className={`coins${className ? ` ${className}` : ""}`}>
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="var(--gold)" />
        <circle
          cx="8"
          cy="8"
          r="4.5"
          fill="none"
          stroke="var(--gold-deep)"
          strokeWidth="1.5"
        />
      </svg>
      <span className="tabular">{amount.toLocaleString()}</span>
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="spinner-wrap" role="status">
      <span className="spinner" aria-hidden="true" />
      {label !== undefined && <span className="spinner-label">{label}</span>}
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
  icon,
  className,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`empty${className ? ` ${className}` : ""}`}>
      {icon !== undefined && <span className="empty-icon" aria-hidden="true">{icon}</span>}
      <p className="empty-title">{title}</p>
      {hint !== undefined && <p className="empty-hint">{hint}</p>}
      {action}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p className="error-note" role="alert">
      {children}
    </p>
  );
}

export function PageHeader({
  title,
  eyebrow,
  subtitle,
  backTo,
  onBack,
  backDisabled = false,
  action,
}: {
  title: string;
  eyebrow?: string;
  subtitle?: string;
  backTo?: string;
  onBack?: () => void;
  backDisabled?: boolean;
  action?: ReactNode;
}) {
  const navigate = useNavigate();
  const { locale } = useLocale();
  return (
    <header className={`page-header${backTo || onBack ? " page-header-with-back" : ""}`}>
      {backTo || onBack ? (
        <button
          className="icon-button"
          type="button"
          aria-label={locale === "zh-CN" ? "返回" : "Back"}
          disabled={backDisabled}
          onClick={() => {
            if (onBack !== undefined) onBack();
            else if (backTo !== undefined) navigate(backTo);
          }}
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
      ) : (
        <span className="page-header-spacer" />
      )}
      <div className="page-header-title">
        {eyebrow !== undefined && <span className="page-header-eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {subtitle !== undefined && <p>{subtitle}</p>}
      </div>
      <span className="page-header-action">{action}</span>
    </header>
  );
}

export function HeaderAction({
  label,
  icon,
  onClick,
  disabled = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      className="header-action"
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="header-action-icon" aria-hidden="true">{icon}</span>
      <span className="header-action-label">{label}</span>
    </button>
  );
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div className={`segmented${className ? ` ${className}` : ""}`} role="tablist" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={value === option.value ? "segmented-on" : ""}
          tabIndex={value === option.value ? 0 : -1}
          onKeyDown={(event) => {
            const index = options.findIndex((item) => item.value === option.value);
            const next = event.key === "ArrowRight" ? (index + 1) % options.length
              : event.key === "ArrowLeft" ? (index + options.length - 1) % options.length
              : event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : null;
            if (next === null) return;
            event.preventDefault();
            onChange(options[next]!.value);
            (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
          }}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function StateBanner({
  tone = "info",
  children,
}: {
  tone?: "info" | "warning" | "danger" | "success";
  children: ReactNode;
}) {
  return (
    <div
      className={`state-banner state-banner-${tone}`}
      role={tone === "danger" ? "alert" : "status"}
    >
      {children}
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  const { locale } = useLocale();
  return (
    <div
      className="skeleton"
      aria-label={locale === "zh-CN" ? "加载中" : "Loading"}
      role="status"
    >
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}

export function BottomSheet({
  open,
  title,
  onClose,
  footer,
  size = "default",
  className,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  size?: "default" | "wide";
  className?: string;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();
  const { locale } = useLocale();
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || panel.current === null) return;
      const controls = [
        ...panel.current.querySelectorAll<HTMLElement>(
          "button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex='-1'])",
        ),
      ].filter((item) => !item.hasAttribute("hidden"));
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("sheet-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("sheet-open");
      previous?.focus();
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div
      className="sheet-layer"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`bottom-sheet bottom-sheet-${size}${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panel}
      >
        <div className="sheet-handle" />
        <header>
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label={locale === "zh-CN" ? "关闭" : "Close"}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <div className="sheet-body">{children}</div>
        {footer !== undefined && <div className="sheet-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
